import { useMemo } from "react";
import { z } from "zod";
import { GraphQL } from "@rpi/openapi-api";

export const ProductAttributesSchema = z.record(
  z.string(),
  z.string().or(z.number()).or(z.boolean()).or(z.undefined())
);

export type ProductAttributes = z.infer<typeof ProductAttributesSchema>;

// same as the graphql type but with validated (enforced flat string to scalar) attributes
export type StandardProduct = Omit<GraphQL.ListOpenApiProductsQuery["listOpenApiProducts"][number], "skuAttributes"> & {
  attributes: ProductAttributes;
};

export const useStandardProducts = () => {
  const { data, isLoading, isError, error } = GraphQL.useListOpenApiProductsQuery({
    hideAlreadyAssociatedProducts: false,
  });

  const standardProducts = useMemo(
    () =>
      data?.listOpenApiProducts
        .map<StandardProduct | null>((product) => {
          if (!product.skuAttributes) {
            return null;
          }

          return {
            ...product,
            attributes: ProductAttributesSchema.parse(product.skuAttributes),
          };
        })
        .filter((product): product is StandardProduct => product !== null),
    [data]
  );

  return { standardProducts, isLoading, isError, error };
};

export function filterByMatchingAttributes(products: StandardProduct[], requiredAttributes: ProductAttributes) {
  return products.filter((product) => {
    return Object.keys(requiredAttributes).every(
      (key) => product.attributes[key] === requiredAttributes[key] || requiredAttributes[key] === undefined
    );
  });
}

export function findAvailableAttributeValues(products: StandardProduct[], attributeKey: string) {
  return Array.from(
    new Set(
      products
        .map((product) => product.attributes[attributeKey])
        .filter((attributeValue): attributeValue is string | number | boolean => attributeValue !== undefined)
    )
  );
}
